import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import { IngredientsSlider } from 'Components/IngredientsSlider';
import { MetaData } from 'Components/MetaData';
import { TextImage } from 'Components/TextImage';

import { productsCategories, productsSlugs, productsSubCategories } from 'constants/products';

import Blog from 'Blocks/Blog';
import { Faq } from 'Blocks/Faq';
import { HowItWorks } from 'Blocks/HowItWorks';
import { NoListBlock } from 'Blocks/NoListBlock';
import { Numbers } from 'Blocks/Numbers';
import ProductProTip from 'Blocks/ProductProTip';
import { ProductsCarousel } from 'Blocks/ProductsCarousel';
import { ProductPageHero } from '../Blocks/ProductPageHero';

import IngredientsCTA from 'Containers/IngredientsCTA';
import ScrollTracker from 'Containers/ScrollTracker';

import { srcOrNextImageProp } from 'PropTypes/imageProps';
import productProps from 'PropTypes/productProps';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import { productsMetadata } from 'assets/metadata/productsMetadata';
import productsStucturedData from 'assets/metadata/productsStructuredData';

import {
  getFilteredFetchedHaircareProductsIsLoaded,
  getHaircareProductsPageContent,
} from 'dux/products/selectors';
import { useTrackProductViewed } from 'dux/products/hooks';

const prepareMetadataProduct = productSlug => productsMetadata[productSlug] ?? {};
const prepareMetadataStructuredDataProduct = productSlug =>
  productsStucturedData[productSlug] ?? {};

const StyledNumbers = styled(Numbers)`
  width: 100%;
`;

const ProductDetails = ({ content, filteredFetchedProductsIsLoaded, productSlug }) => {
  const { noListModule, formulaModule, heroModule, numbersModule } = content;

  useTrackProductViewed({
    category: productsCategories.HAIRCARE,
    subcategory: productsSubCategories.TOPICALS,
    product: heroModule.product,
  });

  return (
    <>
      <MetaData
        {...prepareMetadataProduct(productSlug)}
        structuredData={prepareMetadataStructuredDataProduct(productSlug)}
      />
      <BaseScene footer FooterProps={{ hideCTA: true }} header>
        <ScrollTracker>
          <ProductPageHero content={content.heroModule} />
          {(productSlug === productsSlugs.CURL_CREAM ||
            productSlug === productsSlugs.STYLING_GEL) && (
            <StyledNumbers
              backgroundColor="darkerBeige"
              disclaimer={numbersModule.disclaimer}
              hasBorder
              LabelProps={{ color: 'vert', variant: 'p1' }}
              NumberProps={{ color: 'vert', variant: 'h2' }}
              numbers={numbersModule.numbers}
              title={numbersModule.title}
              TitleProps={{
                color: 'vert',
                marginBottom: 40,
                variantDesktop: 'h2',
                variantMobile: 'h3',
              }}
              variant="3up"
            />
          )}
          <TextImage
            contentAlign="center"
            description={formulaModule.description}
            descriptionProps={{ variant: 'p2' }}
            imageAlt={formulaModule.hero.alt}
            imageDesktop={formulaModule.hero.imageDesktop}
            imageMobile={formulaModule.hero.imageMobile}
            title={formulaModule.header}
            titleProps={{ variant: 'h2', markupName: 'h2' }}
            wideScreenOrder="21"
          >
            <IngredientsSlider
              slides={content.sampleFormulasSlider}
              TitleProps={{ color: theme.colors.neutral[800] }}
            />
            <IngredientsCTA
              category={productsCategories.HAIRCARE}
              dataFrom="formula-module"
              variant="underlined"
            />
          </TextImage>
          {content.proTips && (
            <ProductProTip content={content.proTips} productLabel={heroModule.product.label} />
          )}
          {content.blogPosts && <Blog content={content.blogPosts} />}
          <NoListBlock content={noListModule} />
          <HowItWorks cta dataFrom="how-it-works-module" isHaircareOnly />
          <Faq
            markupName="h3"
            questions={content.faq}
            shouldGenerateJsonld
            title={content.faqModule.header}
          />
          {filteredFetchedProductsIsLoaded && (
            <ProductsCarousel background="veryLightPink" content={content.productsModule} />
          )}
        </ScrollTracker>
      </BaseScene>
    </>
  );
};

ProductDetails.propTypes = {
  content: PropTypes.shape({
    heroModule: PropTypes.shape({
      product: productProps.isRequired,
    }).isRequired,
    formulaModule: PropTypes.shape({
      description: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      hero: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        imageDesktop: srcOrNextImageProp.isRequired,
        imageMobile: srcOrNextImageProp.isRequired,
      }).isRequired,
    }).isRequired,
    blogPosts: PropTypes.shape({}),
    sampleFormulasSlider: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    faq: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    faqModule: PropTypes.shape({
      header: PropTypes.string.isRequired,
      backgroundColor: PropTypes.string,
    }).isRequired,
    productsModule: PropTypes.shape({
      backgroundColor: PropTypes.string,
    }),
    noListModule: PropTypes.shape({
      cta: PropTypes.shape({
        label: PropTypes.string,
        link: PropTypes.string,
      }).isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        imageDesktop: srcOrNextImageProp.isRequired,
        imageMobile: srcOrNextImageProp.isRequired,
      }),
      list: PropTypes.arrayOf(PropTypes.shape({})),
      title: PropTypes.string.isRequired,
    }),
    proTips: PropTypes.arrayOf(PropTypes.shape({})),
    numbersModule: PropTypes.shape({
      disclaimer: PropTypes.string,
      numbers: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string, number: PropTypes.string })
      ),
      title: PropTypes.string,
    }).isRequired,
  }).isRequired,
  filteredFetchedProductsIsLoaded: PropTypes.bool.isRequired,
  productSlug: PropTypes.string.isRequired,
};

const ConnectedHaircareProductDetails = connect((state, { productSlug }) => ({
  content: getHaircareProductsPageContent(state, { productSlug }),
  filteredFetchedProductsIsLoaded: getFilteredFetchedHaircareProductsIsLoaded(state),
}))(ProductDetails);

ConnectedHaircareProductDetails.propTypes = {
  productSlug: PropTypes.string.isRequired,
};

export default ConnectedHaircareProductDetails;
